import { useState, useEffect } from "react";
import axios from "axios";
import { Users, Plus, Edit2, Trash2, Shield, Eye, Briefcase, Power, PowerOff, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getRoleIcon = (role) => {
  switch(role) {
    case 'admin': return <Shield className="w-4 h-4" />;
    case 'manager': return <Briefcase className="w-4 h-4" />;
    case 'viewer': return <Eye className="w-4 h-4" />;
    default: return null;
  }
};

const getRoleBadgeClass = (role) => {
  switch(role) {
    case 'admin': return 'bg-red-100 text-red-800 border-red-200';
    case 'manager': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'viewer': return 'bg-gray-100 text-gray-800 border-gray-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "viewer",
    password: "",
    warehouse_ids: []
  });

  useEffect(() => {
    fetchUsers();
    fetchWarehouses();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${API}/admin/warehouses`, { withCredentials: true });
      setWarehouses(response.data);
    } catch (error) {
      console.error("Failed to load warehouses:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        const updateData = { name: formData.name, role: formData.role };
        if (formData.password) updateData.password = formData.password;
        
        await axios.put(`${API}/users/${editingUser.user_id}`, updateData, { withCredentials: true });
        
        // Update warehouse assignments
        if (formData.warehouse_ids.length > 0) {
          await axios.post(`${API}/admin/user-warehouses?user_id=${editingUser.user_id}`, 
            formData.warehouse_ids, 
            { 
              withCredentials: true,
              headers: { 'Content-Type': 'application/json' }
            }
          );
        }
        
        toast.success("User updated successfully");
      } else {
        const response = await axios.post(`${API}/users`, formData, { withCredentials: true });
        
        // Assign warehouses to new user
        if (formData.warehouse_ids.length > 0) {
          await axios.post(`${API}/admin/user-warehouses?user_id=${response.data.user_id}`, 
            formData.warehouse_ids,
            { 
              withCredentials: true,
              headers: { 'Content-Type': 'application/json' }
            }
          );
        }
        
        toast.success("User created successfully");
      }
      
      setFormData({ email: "", name: "", role: "viewer", password: "", warehouse_ids: [] });
      setEditingUser(null);
      setIsSheetOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Toggle user enable/disable
  const handleToggleStatus = async (user) => {
    const newStatus = !(user.is_active !== false);
    const action = newStatus ? 'enable' : 'disable';
    
    if (!window.confirm(`Are you sure you want to ${action} user "${user.name}"?`)) return;
    
    try {
      await axios.patch(
        `${API}/users/${user.user_id}/status?is_active=${newStatus}`,
        {},
        { withCredentials: true }
      );
      toast.success(`User ${newStatus ? 'enabled' : 'disabled'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user status");
    }
  };

  const handleEdit = async (user) => {
    setEditingUser(user);
    
    // Fetch user's warehouse assignments
    try {
      const response = await axios.get(`${API}/admin/user-warehouses/${user.user_id}`, { withCredentials: true });
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        password: "",
        warehouse_ids: response.data.warehouse_ids || []
      });
    } catch (error) {
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        password: "",
        warehouse_ids: []
      });
    }
    
    setIsSheetOpen(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-outfit font-semibold text-stone-900">
                User Management
              </CardTitle>
              <CardDescription>Manage user accounts and permissions</CardDescription>
            </div>
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button
                  onClick={() => {
                    setEditingUser(null);
                    setFormData({ email: "", name: "", role: "viewer", password: "", warehouse_ids: [] });
                  }}
                  className="bg-lime-700 text-white hover:bg-lime-800"
                  data-testid="add-user-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />Add User
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>{editingUser ? "Edit User" : "Add New User"}</SheetTitle>
                  <SheetDescription>
                    {editingUser ? "Update user information and role" : "Create a new user account"}
                  </SheetDescription>
                </SheetHeader>
                <form onSubmit={handleSubmit} className="space-y-6 mt-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      disabled={!!editingUser}
                      required={!editingUser}
                      data-testid="user-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="user-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role *</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                      <SelectTrigger data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">Viewer (Read Only)</SelectItem>
                        <SelectItem value="manager">Manager (Can Edit)</SelectItem>
                        <SelectItem value="admin">Admin (Full Access)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password {!editingUser && "*"}</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      placeholder={editingUser ? "Leave blank to keep current" : ""}
                      data-testid="user-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Warehouse Access *</Label>
                    <div className="border border-stone-200 rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                      {warehouses.length === 0 ? (
                        <p className="text-sm text-stone-500">No warehouses available</p>
                      ) : (
                        warehouses.map((warehouse) => (
                          <label key={warehouse.id} className="flex items-center gap-2 cursor-pointer hover:bg-stone-50 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={formData.warehouse_ids.includes(warehouse.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, warehouse_ids: [...formData.warehouse_ids, warehouse.id] });
                                } else {
                                  setFormData({ ...formData, warehouse_ids: formData.warehouse_ids.filter(id => id !== warehouse.id) });
                                }
                              }}
                              className="w-4 h-4 text-lime-700 rounded"
                            />
                            <span className="text-sm">{warehouse.warehouse_name}</span>
                          </label>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-stone-500">Select warehouses this user can access</p>
                  </div>
                  <SheetFooter>
                    <Button type="submit" className="bg-lime-700 text-white hover:bg-lime-800" data-testid="save-user-btn">
                      {editingUser ? "Update" : "Create"} User
                    </Button>
                  </SheetFooter>
                </form>
              </SheetContent>
            </Sheet>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-left">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`} className={user.is_active === false ? 'opacity-60 bg-stone-50' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {user.name}
                        {user.is_active === false && <UserX className="w-4 h-4 text-stone-400" />}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeClass(user.role)}`}>
                        {getRoleIcon(user.role)}
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {user.is_active === false ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-stone-200 text-stone-600">
                          Disabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-lime-100 text-lime-700">
                          Active
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-stone-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-left">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          onClick={() => handleEdit(user)}
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-user-${user.user_id}`}
                          title="Edit User"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {user.user_id !== currentUser?.user_id && (
                          <>
                            <Button
                              onClick={() => handleToggleStatus(user)}
                              variant="ghost"
                              size="sm"
                              className={user.is_active === false ? "text-lime-600 hover:text-lime-700" : "text-amber-600 hover:text-amber-700"}
                              data-testid={`toggle-user-${user.user_id}`}
                              title={user.is_active === false ? "Enable User" : "Disable User"}
                            >
                              {user.is_active === false ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                            </Button>
                            <Button
                              onClick={() => handleDelete(user.user_id)}
                              variant="ghost"
                              size="sm"
                              className="text-rose-600 hover:text-rose-700"
                              data-testid={`delete-user-${user.user_id}`}
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;